'use client';
/**
 * L'ÉTAT D'AVANCEMENT d'une action du plan (RQ, 07/10/2026).
 *
 * Même lecture que la frise des NC — mêmes pastilles numérotées, mêmes dates
 * sous les titres — mais les étapes d'une action ne sont PAS ses onglets : trois
 * d'entre elles se jouent dans « Suivi ». Chaque étape mène donc à l'onglet où
 * elle se joue, et la frise reste un état d'avancement, pas une barre d'onglets.
 *
 * La logique (où en est-on, quel geste suit) est dans `lib/parcours-action.ts`.
 */
import React from 'react';
import { I } from '@/components/ui';
import { fmtDate } from '@/lib/meta';
import {
  ETAPES_ACTION, NB_ETAPES_ACTION, etapeAction, etatEtapeAction, etapeCouranteAction,
  numeroAction, parcoursInterrompu, type EtapeAction, type OngletEtapeAction,
} from '@/lib/parcours-action';

export function FriseAction({ statut, dates, onAller }: {
  statut: string;
  dates: Partial<Record<EtapeAction, string | undefined | null>>;
  /** Mène à l'onglet où l'étape se joue. */
  onAller: (onglet: OngletEtapeAction) => void;
}) {
  const interrompu = parcoursInterrompu(statut);
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {interrompu && (
        <div style={{ fontSize: 12.5, color: 'var(--red-600)', fontWeight: 600 }}>
          Parcours interrompu — action annulée. Aucune étape n’a été menée à son terme.
        </div>
      )}
      <ol aria-label="Avancement de l’action"
        style={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', gap: 0, listStyle: 'none', margin: 0, padding: 0 }}>
        {ETAPES_ACTION.map((e) => {
          const etat = etatEtapeAction(e.cle, statut);
          const faite = etat === 'faite';
          const encours = etat === 'en_cours';
          return (
            <li key={e.cle} style={{ minWidth: 0 }}>
              <button type="button" onClick={() => onAller(e.onglet)} className="ged-btn"
                aria-current={encours ? 'step' : undefined}
                title={`${e.question} — ${faite ? 'étape franchie' : encours ? 'étape en cours' : 'étape à venir'}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 9, border: 'none', cursor: 'pointer',
                  background: 'transparent', fontFamily: 'var(--font-sans)', padding: '7px 16px 7px 10px',
                  color: encours ? 'var(--color-primary-active)' : 'var(--text-muted)',
                }}>
                <span aria-hidden style={{
                  display: 'grid', placeItems: 'center', width: 24, height: 24, borderRadius: '50%', flex: 'none',
                  fontSize: 12, fontWeight: 800,
                  background: faite ? 'var(--color-primary)' : encours ? 'var(--cyan-50)' : '#fff',
                  color: faite ? '#fff' : encours ? 'var(--color-primary-active)' : 'var(--text-muted)',
                  border: `2px solid ${faite || encours ? 'var(--color-primary)' : 'var(--border-strong)'}`,
                }}>{faite ? <I n="check" s={12} /> : numeroAction(e.cle)}</span>
                <span style={{ display: 'grid', textAlign: 'start', lineHeight: 1.25 }}>
                  <span style={{ fontSize: 13.5, fontWeight: encours ? 700 : 600 }}>{e.titre}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-faint, var(--text-muted))' }}>
                    {dates[e.cle] ? fmtDate(dates[e.cle] as string) : (encours ? 'en cours' : 'à venir')}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** « Étape 2 sur 4 — En cours : où en est le travail ? » + ce qu'on attend + ce qui manque. */
export function ConsigneEtapeAction({ statut, reste }: { statut: string; reste: string[] }) {
  const cle = etapeCouranteAction(statut);
  const e = etapeAction(cle);
  const interrompu = parcoursInterrompu(statut);
  if (interrompu) return null;
  return (
    <div style={{
      background: 'var(--cyan-50)', borderRadius: 'var(--radius-md)', padding: '10px 14px',
      borderInlineStart: '3px solid var(--color-primary)', display: 'grid', gap: 3,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>
        Étape {numeroAction(cle)} sur {NB_ETAPES_ACTION}
        {statut === 'verifiee' ? ' · parcours achevé' : ' · en cours'}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>{e.titre} — {e.question}</div>
      {/* Une fois l'action clôturée, la consigne de l'étape parlerait au futur
          d'un geste déjà posé : on dit ce qui EST, pas ce qu'il reste à faire. */}
      <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-body)' }}>
        {statut === 'verifiee'
          ? 'L’action est clôturée : son efficacité a été constatée et signée. Rien ne peut plus la faire redescendre d’état.'
          : e.consigne}
      </div>
      {reste.length > 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--amber-700, #8A5B00)', fontWeight: 600 }}>À compléter : {reste.join(' · ')}.</div>
      )}
    </div>
  );
}
